// src/slices/authSlice.js

import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import {axiosInstance, axiosWithoutInterceptors} from '../routes/axiosInstance';
import {
  clearRawData,
  clearSessionRawData,
  clearUserForecastData,
  clearIndividualUserForecasts,
  clearHistoricalData,
  setSelectedForecast
} from './dataSlice';
import {clearMarketSessions, clearLastFetchSignature} from './marketSlice';
import {clearReportsState} from './reportsSlice';
import {clearUserMessages} from './userSlice';

// At the top, parse the user object from localStorage or sessionStorage
let storedUser = null;
let storedManagedResources = [];
let storedToken = null;
let storedRefreshToken = null;

// True when a JWT's exp claim is in the past, or the token is unreadable.
// Decode only, no signature check: this just spots sessions that are dead.
const jwtExpired = (token) => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(payload));
    return typeof exp !== 'number' || exp * 1000 <= Date.now();
  } catch (err) {
    return true;
  }
};

try {
  // Check both localStorage and sessionStorage for tokens and user
  storedToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  storedRefreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');

  // A session whose refresh token has expired cannot be revived, so drop it
  // now. Kept, its dead bearer token gets a 401 even from public endpoints;
  // the interceptor then bounces a homepage visitor to /signin?next=/ and
  // they land back on the homepage after signing in.
  if (storedRefreshToken && jwtExpired(storedRefreshToken)) {
    ['authToken', 'refreshToken', 'user', 'rememberMe', 'managedResources']
      .forEach((key) => localStorage.removeItem(key));
    ['authToken', 'refreshToken', 'user']
      .forEach((key) => sessionStorage.removeItem(key));
    storedToken = null;
    storedRefreshToken = null;
  }

  const rawSessionUser = localStorage.getItem('user') || sessionStorage.getItem('user');
  if (rawSessionUser) {
    const parsed = JSON.parse(rawSessionUser);
    // Determine role based on boolean flags (priority order: admin > read_only > is_forecaster)
    const isAdmin = parsed.is_admin === true;
    const isReadOnly = parsed.read_only === true;
    const isForecaster = parsed.is_forecaster === true;
    // If not admin, read_only, or forecaster, then it's market_owner
    const isMarketOwner = !isAdmin && !isReadOnly && !isForecaster;

    const role = isAdmin ? 'admin' :
                 isReadOnly ? 'market_owner_read' :
                 isForecaster ? 'forecaster' :
                 'market_owner';

    storedUser = {
      id: parsed.user_id, // #noqa
      username: parsed.username,
      role,
      isAdmin,
      isMarketOwnerReadOnly: isReadOnly,
      isMarketOwner,
      isForecaster,
      raw: parsed,
    };
  }

  // Load managed resources from localStorage (only stored in localStorage, not sessionStorage)
  const rawManagedResources = localStorage.getItem('managedResources');
  if (rawManagedResources) {
    storedManagedResources = JSON.parse(rawManagedResources);
  }
} catch (err) {
  storedUser = null;
  storedManagedResources = [];
  storedToken = null;
  storedRefreshToken = null;
}

// Helper to detect where tokens should be stored based on a remember flag saved in localStorage
const storageIsLocal = () => localStorage.getItem('rememberMe') === 'true';

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password, rememberMe }, { rejectWithValue }) => {
    try {
      // Authenticate user and get tokens
      const response = await axiosWithoutInterceptors.post(
        '/token',
        { email, password },
        { headers: { 'X-Client-Source': 'web' } }
      );

      if (response.status === 200) {
        const { access: accessToken, refresh: refreshToken, user: userData } = response.data;

        // Store tokens in the appropriate storage based on rememberMe
        try {
          // Re-arm the performance-pane hint so it plays once per login
          // (PerformancePaneHandle sets this flag after showing it).
          sessionStorage.removeItem('performance-pane-hint-shown');
          if (rememberMe) {
            localStorage.setItem('authToken', accessToken);
            localStorage.setItem('refreshToken', refreshToken);
            localStorage.setItem('rememberMe', 'true');
            // store user in localStorage
            localStorage.setItem('user', JSON.stringify(userData));
            // remove any session copies
            sessionStorage.removeItem('authToken');
            sessionStorage.removeItem('refreshToken');
            sessionStorage.removeItem('user');
          } else {
            // session-only: use sessionStorage
            sessionStorage.setItem('authToken', accessToken);
            sessionStorage.setItem('refreshToken', refreshToken);
            sessionStorage.removeItem('rememberMe');
            sessionStorage.setItem('user', JSON.stringify(userData));
            // remove any local copies
            localStorage.removeItem('authToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            localStorage.removeItem('rememberMe');
          }
        } catch (err) {
          // ignore storage errors
        }

        // Return tokens and user + remember flag so reducers can update state
        return { accessToken, refreshToken, userData, rememberMe };
      }
    } catch (error) {
      if (error.response && error.response.data) {
        if (error.response.data.non_field_errors) {
          // Handle non_field_errors
          return rejectWithValue(error.response.data.non_field_errors[0]);
        } else if (error.response.data.detail) {
          // Handle other error messages
          return rejectWithValue(error.response.data.detail);
        } else {
          return rejectWithValue('An unexpected error occurred.');
        }
      } else {
        return rejectWithValue('Something went wrong. Please try again.');
      }
    }
  }
);

// Fetch managed resources and roles for non-forecasters
export const fetchManagedResourceRoles = createAsyncThunk(
  'auth/fetchManagedResourceRoles',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/user/resource-manager');
      const resourceData = response.data?.data || response.data || [];

      // Transform data to extract unique resources with their roles
      const resourceRolesMap = new Map();
      resourceData.forEach(item => {
        const resourceId = item.resource;
        const role = item.role; // 'Forecaster', 'Market Owner', etc.

        if (!resourceRolesMap.has(resourceId)) {
          resourceRolesMap.set(resourceId, {
            resourceId: resourceId,
            resourceName: item.resource_name,
            role: role,
            userId: item.user,
            userEmail: item.user_email,
            createdAt: item.created_at
          });
        }
      });

      return Array.from(resourceRolesMap.values());
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch managed resources');
    }
  }
);

// One shared in-flight refresh request, so a burst of parallel 401s issues
// a single call to /token/refresh instead of one per failed request.
let inflightRefresh = null;

// Rejection marker for a refresh that resolved after logout or an account
// switch. Consumers must treat it as "drop the result", not as a failed
// session (see the rejected reducer and the axios 401 interceptor).
export const REFRESH_SESSION_CHANGED = 'session-changed';

export const refreshAccessToken = createAsyncThunk(
  'auth/refreshAccessToken',
  async (_, { getState, rejectWithValue }) => {
    // Refresh must bypass the 401 interceptor: going through axiosInstance
    // would both attach the expired access token and re-enter the
    // interceptor recursively when the refresh itself gets a 401.
    if (!inflightRefresh) {
      const { refreshToken } = getState().auth;
      inflightRefresh = {
        refreshToken,
        promise: axiosWithoutInterceptors
          .post('/token/refresh/', { refresh: refreshToken })
          .finally(() => { inflightRefresh = null; }),
      };
    }
    const started = inflightRefresh;
    try {
      const response = await started.promise;
      // The session may have changed while the request was in flight
      // (logout, or login as another user). Persisting the result then
      // would resurrect the old session in storage.
      const { auth } = getState();
      if (!auth.isAuth || auth.refreshToken !== started.refreshToken) {
        return rejectWithValue(REFRESH_SESSION_CHANGED);
      }
      const { access: accessToken } = response.data;
      // Store the new access token in the same storage type used previously
      try {
        if (storageIsLocal()) {
          localStorage.setItem('authToken', accessToken);
        } else {
          sessionStorage.setItem('authToken', accessToken);
        }
      } catch (err) {
        // ignore storage errors
      }
      return accessToken;
    } catch (error) {
      return rejectWithValue('Failed to refresh access token.');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { dispatch }) => {
    // Remove stored data from both storages for safety
    try {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('managedResources');
    } catch (err) {}
    try {
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('refreshToken');
      sessionStorage.removeItem('user');
    } catch (err) {}

    // Dispatch actions to clear data from all slices
    // Data slice - clear all data including session data
    dispatch(clearRawData());
    dispatch(clearSessionRawData());
    dispatch(clearUserForecastData());
    dispatch(clearIndividualUserForecasts());
    dispatch(clearHistoricalData());
    dispatch(setSelectedForecast([{ id: null, name: 'Ensemble' }]));

    // Market slice
    dispatch(clearMarketSessions());
    dispatch(clearLastFetchSignature());

    // Reports slice
    dispatch(clearReportsState());

    // User slice
    dispatch(clearUserMessages());

    // Finally, clear auth state
    dispatch(logout());
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    isAuth: (storedToken !== null),
    token: storedToken,
    refreshToken: storedRefreshToken,
    // persist a normalized user object in state so role checks survive reloads
    user: storedUser || null,
    username: storedUser ? storedUser.username : '',
    isAdmin: storedUser ? !!storedUser.isAdmin : false,
    isMarketOwner: storedUser ? !!storedUser.isMarketOwner : false,
    isMarketOwnerReadOnly: storedUser ? !!storedUser.isMarketOwnerReadOnly : false,
    isForecaster: storedUser ? !!storedUser.isForecaster : false,
    role: storedUser ? storedUser.role : '',
    // Managed resources with roles for permission checking
    managedResources: storedManagedResources || [], // [{resourceId, resourceName, role, userId, userEmail, createdAt}]
    managedResourcesLoading: false,
    errorMessage: '',
    loading: false,
  },
  reducers: {
    // Synchronous logout action
    logout(state) {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuth = false;
      state.username = '';
      state.role = '';
      state.isAdmin = false;
      state.isMarketOwner = false;
      state.isMarketOwnerReadOnly = false;
      state.isForecaster = false;
      state.managedResources = [];
      state.managedResourcesLoading = false;
      try {
        localStorage.removeItem('user');
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('rememberMe');
      } catch (err) {}
      try { sessionStorage.removeItem('user'); sessionStorage.removeItem('authToken'); sessionStorage.removeItem('refreshToken'); } catch (err) {}
      state.successMessage = 'Logged out successfully.';
      state.error = null;
    },
    clearAuthMessages(state) {
      state.error = null;
      state.successMessage = null;
    },
    setErrorMessage(state, action) {
      state.errorMessage = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle login actions
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.errorMessage = '';
      })
      .addCase(login.fulfilled, (state, action) => {
        // Normalize and persist the returned user data into state
        const ud = action.payload?.userData || {};

        // Determine role based on boolean flags (priority order: admin > read_only > is_forecaster)
        // Same logic as initial state parsing
        const isAdmin = ud.is_admin === true;
        const isForecaster = ud.is_forecaster === true;
        // If not admin or forecaster, then it's market_owner
        const isMarketOwner = !isAdmin && !isForecaster;

        const role = isAdmin ? 'admin' :
                     isForecaster ? 'forecaster' :
                     'market_owner';

        const normalized = {
          id: ud.user_id,
          username: ud.username,
          role,
          isAdmin,
          isMarketOwner,
          isForecaster,
          raw: ud,
        };

        state.role = normalized.role;
        state.loading = false;
        state.isAuth = true;
        state.token = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.user = normalized;
        state.username = normalized.username;
        state.isAdmin = normalized.isAdmin;
        state.isMarketOwner = normalized.isMarketOwner;
        state.isForecaster = normalized.isForecaster;
        // note: the login thunk already stored the raw user object in storage
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload || 'Login failed.';
      })
      // Handle refresh token actions
      .addCase(refreshAccessToken.fulfilled, (state, action) => {
        state.token = action.payload;
      })
      .addCase(refreshAccessToken.rejected, (state, action) => {
        // A stale refresh (finished after logout/account switch) must not
        // clear whatever session replaced it.
        if (action.payload === REFRESH_SESSION_CHANGED) return;
        state.isAuth = false;
        state.token = null;
        state.refreshToken = null;
        try { localStorage.removeItem('authToken'); localStorage.removeItem('refreshToken'); } catch (err) {}
        try { sessionStorage.removeItem('authToken'); sessionStorage.removeItem('refreshToken'); } catch (err) {}
      })
      // Handle fetchManagedResourceRoles actions
      .addCase(fetchManagedResourceRoles.pending, (state) => {
        state.managedResourcesLoading = true;
      })
      .addCase(fetchManagedResourceRoles.fulfilled, (state, action) => {
        state.managedResourcesLoading = false;
        state.managedResources = action.payload;
        // Store in localStorage for persistence across refreshes
        try {
          localStorage.setItem('managedResources', JSON.stringify(action.payload));
        } catch (err) {
          // ignore storage errors
        }
      })
      .addCase(fetchManagedResourceRoles.rejected, (state, action) => {
        state.managedResourcesLoading = false;
        state.managedResources = [];
        state.errorMessage = action.payload || 'Failed to fetch managed resources';
      });
  },
});

export const { logout, clearAuthMessages, setErrorMessage } = authSlice.actions;
export default authSlice.reducer;
// Export selectors

export const selectAuthUser = (state) => state.auth.user;
export const selectAuthToken = (state) => state.auth.token;
export const selectAuthRefreshToken = (state) => state.auth.refreshToken;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectAuthSuccessMessage = (state) => state.auth.successMessage;
export const selectAuthIsAdmin = (state) => state.auth.isAdmin;
export const selectAuthIsMarketOwner = (state) => state.auth.isMarketOwner;
export const selectAuthIsMarketOwnerReadOnly = (state) => state.auth.isMarketOwnerReadOnly;
export const selectAuthIsForecaster = (state) => state.auth.isForecaster;
export const selectAuthIsAuth = (state) => state.auth.isAuth;
export const selectAuthUsername = (state) => state.auth.username;
export const selectAuthRole = (state) => state.auth.role;

// Managed resources selectors
export const selectManagedResourcesAuth = (state) => state.auth.managedResources;
export const selectManagedResourcesLoading = (state) => state.auth.managedResourcesLoading;

// Helper selector: Get role for a specific resource
export const selectRoleForResource = (resourceId) => (state) => {
  const managedResources = state.auth.managedResources || [];
  const resource = managedResources.find(r => r.resourceId === resourceId);
  return resource ? resource.role : null;
};

// Helper selector: Check if user manages a specific resource
export const selectManagesResource = (resourceId) => (state) => {
  const managedResources = state.auth.managedResources || [];
  return managedResources.some(r => r.resourceId === resourceId);
};

// Helper selector: Get all resource IDs the user manages
export const selectManagedResourceIds = (state) => {
  const managedResources = state.auth.managedResources || [];
  return managedResources.map(r => r.resourceId);
};
